'use client'

// Client wrapper around the media list that adds a type filter (tabs) on top of
// `MediaDocCard`. Used by home media sections (codelogician.dev, app-www) that
// want a curated, filterable grid. The full archive lives on the /media page.

import { useMemo, useState } from 'react'
import { Join } from '@/lib/utils'
import { MediaDocCard, type MediaDocCardType } from '@/shared/components/ui/media-doc-card'
import {
  type MediaItem,
  type MediaType,
  filterMedia,
  mediaTypesPresent,
  sortByDateDesc,
  type MediaProduct,
} from '@/shared/components/data/imandra-media'

const TYPE_LABELS: Record<MediaType, string> = {
  news: 'News',
  article: 'Articles',
  blog: 'Blog',
  video: 'Videos',
  paper: 'Research',
  'white-paper': 'White papers',
}

const COLUMN_CLASSES: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 lg:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
}

export interface MediaFilterSectionProps {
  /** Explicit items; when omitted the canonical list is filtered via props. */
  items?: MediaItem[]
  /** Restrict to items relevant to ANY of these products. */
  products?: MediaProduct[]
  /** Restrict to featured items. */
  featured?: boolean
  /** Cap the number of items shown (after sorting). */
  limit?: number
  /** Columns at the largest breakpoint (1, 2 or 3). Default 3. */
  columns?: 1 | 2 | 3
  /** Show the type filter tabs. Default true. */
  showFilter?: boolean
  /** Label for the "all types" tab. Default "All". */
  allLabel?: string
  className?: string
}

export function MediaFilterSection({
  items,
  products,
  featured,
  limit,
  columns = 3,
  showFilter = true,
  allLabel = 'All',
  className,
}: MediaFilterSectionProps) {
  const base = useMemo(
    () =>
      sortByDateDesc(
        items ? filterMedia({ products, featured }, items) : filterMedia({ products, featured })
      ),
    [items, products, featured]
  )

  const tabs = useMemo(() => mediaTypesPresent(base), [base])
  const [selected, setSelected] = useState<MediaType | 'all'>('all')

  const visible = useMemo(() => {
    const filtered = selected === 'all' ? base : base.filter((i) => i.type === selected)
    return limit != null ? filtered.slice(0, limit) : filtered
  }, [base, selected, limit])

  return (
    <div className={className}>
      {showFilter && tabs.length > 1 ? (
        <div className="mb-8 flex flex-wrap gap-2">
          {(['all', ...tabs] as const).map((tab) => {
            const isSelected = selected === tab
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setSelected(tab)}
                className={Join(
                  'rounded-full border px-3 py-1 font-mono text-xs tracking-widest uppercase transition-colors',
                  isSelected
                    ? 'border-teal-400/70 bg-teal-400/10 text-teal-600 dark:text-teal-300'
                    : 'border-slate-300 text-slate-500 hover:border-teal-400/50 hover:text-teal-600 dark:border-slate-700/50 dark:text-slate-400 dark:hover:text-teal-300'
                )}
              >
                {tab === 'all' ? allLabel : TYPE_LABELS[tab]}
              </button>
            )
          })}
        </div>
      ) : null}

      <div className={Join('grid gap-4', COLUMN_CLASSES[columns])}>
        {visible.map((item, index) => (
          <MediaDocCard
            key={`${item.url}-${index}`}
            type={item.type as MediaDocCardType}
            title={item.title}
            href={item.url}
            date={item.date}
            description={item.description}
            source={item.source}
            highlight={item.highlight}
          />
        ))}
      </div>
    </div>
  )
}
